import type React from 'react';
import classes from '../work-schedule-page.module.scss';
import type { ShiftScheduleSlot } from '../../../models/schedule.types';
import { getTodayIsoDate, formatDateDot } from '../../../utils/date.utils';
import type { useWorkSchedulePageController } from '../useWorkSchedulePageController';

type ScheduleBoardProps = Pick<
  ReturnType<typeof useWorkSchedulePageController>,
  | 'isLoadingWeek'
  | 'weekError'
  | 'sortedSections'
  | 'canManageSchedule'
  | 'onOpenCreateSectionModal'
  | 'hasActiveFilters'
  | 'visibleSections'
  | 'visibleWeekDays'
  | 'setTipDate'
  | 'onOpenCreateShiftModal'
  | 'onOpenEditSectionModal'
  | 'onDeleteSection'
  | 'isBusy'
  | 'onOpenCreateRoleModal'
  | 'onOpenEditShiftModal'
  | 'onDeleteShift'
  | 'onDuplicateRole'
  | 'onOpenEditRoleModal'
  | 'onDeleteRole'
  | 'onOpenCreateSlotModal'
  | 'employeeInsightSummaries'
  | 'user'
  | 'onOpenEmployeeInsight'
  | 'isLoadingEmployeeInsight'
  | 'onOpenEditSlotModal'
  | 'canAssignEmployees'
>;

export const ScheduleBoard: React.FC<ScheduleBoardProps> = ({
  isLoadingWeek,
  weekError,
  sortedSections,
  canManageSchedule,
  onOpenCreateSectionModal,
  hasActiveFilters,
  visibleSections,
  visibleWeekDays,
  setTipDate,
  onOpenCreateShiftModal,
  onOpenEditSectionModal,
  onDeleteSection,
  isBusy,
  onOpenCreateRoleModal,
  onOpenEditShiftModal,
  onDeleteShift,
  onDuplicateRole,
  onOpenEditRoleModal,
  onDeleteRole,
  onOpenCreateSlotModal,
  employeeInsightSummaries,
  user,
  onOpenEmployeeInsight,
  isLoadingEmployeeInsight,
  onOpenEditSlotModal,
  canAssignEmployees,
}) => (
  <section className={`workspaceMain ${classes.schedulePageCard}`}>
    <div className={classes.schedulePageHeaderRow}>
      <div>
        <h2 className="homeTitle">סידור עבודה שבועי</h2>
        <p className="homeHint">ניהול מחלקות, משמרות, תפקידים וסלוטים</p>
      </div>
    </div>

    {isLoadingWeek && <p className="homeHint">טוען סידור שבועי...</p>}
    {weekError && <p className="errorText inlineMessage">{weekError}</p>}
    {!isLoadingWeek && sortedSections.length === 0 && (
      <div className={classes.scheduleEmptyState}>
        <h2 className={classes.scheduleEmptyTitle}>עדיין לא נבנה סידור עבודה</h2>
        {canManageSchedule && (
          <>
            <p className="emptyStateText">
              התחילו ביצירת מחלקה ראשונה, ואז הוסיפו משמרות, תפקידים וסלוטים לכל יום.
            </p>
            <button type="button" className="button schedulePrimaryButton" onClick={onOpenCreateSectionModal}>
              + יצירת מחלקה ראשונה
            </button>
          </>
        )}
      </div>
    )}

    {!isLoadingWeek && hasActiveFilters && visibleSections.length === 0 && (
      <div className={classes.scheduleEmptyState}>
        <h2 className={classes.scheduleEmptyTitle}>לא נמצאו שיבוצים</h2>
        <p className="emptyStateText">אין שיבוצים התואמים למסננים שנבחרו.</p>
      </div>
    )}

    {(!hasActiveFilters || visibleSections.length > 0) && (
      <div className={classes.scheduleBoardScroll}>
        <div className={classes.scheduleBoard}>
          <div className={classes.scheduleRoleGridHeader}>
            <div className={classes.scheduleRoleHeadCell}>תפקיד / משמרת</div>
            {visibleWeekDays.map((day) => (
              <div key={day.dateDay} className={classes.scheduleDayHeadCell}>
                <span>{day.weekdayLabel}</span>
                <small>{formatDateDot(day.dateDay).slice(0, 5)}</small>
                {canManageSchedule && (
                  <button
                    type="button"
                    className={classes.scheduleTipButton}
                    aria-label={`חלוקת טיפים לתאריך ${day.dateDay}`}
                    onClick={() => setTipDate(day.dateDay)}
                    disabled={day.dateDay > getTodayIsoDate()}
                  >
                    טיפים
                  </button>
                )}
              </div>
            ))}
          </div>

          {visibleSections.map((section) => (
            <article key={section.id} className={classes.scheduleDepartment}>
              <div className={classes.scheduleDepartmentBar}>
                <div>
                  <span className={classes.scheduleDepartmentLabel}>מחלקה</span>
                  <h2>{section.sectionName}</h2>
                </div>
                {canManageSchedule && (
                  <div className={`${classes.scheduleEntityActions} ${classes.scheduleEntityActionsCompact}`}>
                    <button
                      type="button"
                      className={`${classes.scheduleBandButton} ${classes.primary}`}
                      onClick={() => onOpenCreateShiftModal(section.id)}
                    >
                      + משמרת
                    </button>
                    <button
                      type="button"
                      className={classes.scheduleBandButton}
                      onClick={() => onOpenEditSectionModal(section)}
                    >
                      עריכה
                    </button>
                    <button
                      type="button"
                      className={`${classes.scheduleBandButton} ${classes.danger}`}
                      onClick={() => {
                        void onDeleteSection(section.id);
                      }}
                      disabled={isBusy(`delete-section-${section.id}`)}
                    >
                      מחיקה
                    </button>
                  </div>
                )}
              </div>

              {section.shifts.length === 0 && (
                <div className={`${classes.scheduleInlineEmpty} ${classes.scheduleBoardEmpty}`}>
                  <p className="emptyStateText">אין משמרות במחלקה זו.</p>
                  {canManageSchedule && (
                    <button
                      type="button"
                      className="navArrowButton"
                      onClick={() => onOpenCreateShiftModal(section.id)}
                    >
                      + הוספת משמרת ראשונה
                    </button>
                  )}
                </div>
              )}

              {section.shifts.map((shift) => (
                <section key={shift.id} className={classes.scheduleShiftGroup}>
                  <div className={classes.scheduleShiftBar}>
                    <div>
                      <span className={classes.scheduleShiftLabel}>משמרת</span>
                      <h3>{shift.shiftName}</h3>
                    </div>
                    {canManageSchedule && (
                      <div
                        className={`${classes.scheduleEntityActions} ${classes.scheduleEntityActionsCompact}`}
                      >
                        <button
                          type="button"
                          className={classes.scheduleActionButton}
                          onClick={() => onOpenCreateRoleModal(shift.id)}
                        >
                          + תפקיד
                        </button>
                        <button
                          type="button"
                          className={classes.scheduleActionButton}
                          onClick={() => onOpenEditShiftModal(shift)}
                        >
                          עריכה
                        </button>
                        <button
                          type="button"
                          className={`${classes.scheduleActionButton} ${classes.danger}`}
                          onClick={() => {
                            void onDeleteShift(shift.id);
                          }}
                          disabled={isBusy(`delete-shift-${shift.id}`)}
                        >
                          מחיקה
                        </button>
                      </div>
                    )}
                  </div>

                  {shift.roles.length === 0 && (
                    <div
                      className={`${classes.scheduleInlineEmpty} ${classes.scheduleBoardEmpty} ${classes.compact}`}
                    >
                      <p className="emptyStateText">אין תפקידים במשמרת זו.</p>
                      {canManageSchedule && (
                        <button
                          type="button"
                          className="navArrowButton"
                          onClick={() => onOpenCreateRoleModal(shift.id)}
                        >
                          + הוספת תפקיד ראשון
                        </button>
                      )}
                    </div>
                  )}

                  {shift.roles.map((role) => {
                    const slotsByDate = new Map<string, ShiftScheduleSlot>();
                    for (const slot of role.slots) {
                      if (!slotsByDate.has(slot.dateDay)) {
                        slotsByDate.set(slot.dateDay, slot);
                      }
                    }

                    return (
                      <div key={role.id} className={classes.scheduleRoleRow}>
                        <div className={classes.scheduleRoleNameCell}>
                          <div className={classes.scheduleRoleTextWrap}>
                            <p className={classes.scheduleEntityBadge}>תפקיד</p>
                            <h4 className={classes.scheduleRoleName}>{role.roleName}</h4>
                          </div>
                          {canManageSchedule && (
                            <div className={classes.scheduleRoleActions}>
                              <button
                                type="button"
                                className={`${classes.scheduleActionButton} ${classes.scheduleActionButtonCompact}`}
                                onClick={() => {
                                  void onDuplicateRole(shift.id, role);
                                }}
                                disabled={isBusy(`duplicate-role-${role.id}`)}
                              >
                                שכפל
                              </button>
                              <button
                                type="button"
                                className={`${classes.scheduleActionButton} ${classes.scheduleActionButtonCompact}`}
                                onClick={() => onOpenEditRoleModal(role)}
                              >
                                עריכה
                              </button>
                              <button
                                type="button"
                                className={`${classes.scheduleActionButton} ${classes.danger} ${classes.scheduleActionButtonCompact}`}
                                onClick={() => {
                                  void onDeleteRole(role.id);
                                }}
                                disabled={isBusy(`delete-role-${role.id}`)}
                              >
                                מחיקה
                              </button>
                            </div>
                          )}
                        </div>

                        {visibleWeekDays.map((day) => {
                          const slot = slotsByDate.get(day.dateDay);

                          if (!slot) {
                            return (
                              <div
                                key={`${role.id}-${day.dateDay}`}
                                data-label={`${day.weekdayLabel} · ${formatDateDot(day.dateDay)}`}
                                className={`${classes.scheduleSlotCell} ${classes.empty}`}
                              >
                                {canManageSchedule && (
                                  <button
                                    type="button"
                                    className={classes.scheduleAddSlotButton}
                                    onClick={() => onOpenCreateSlotModal(role.id, day.dateDay)}
                                    aria-label={`הוספת סלוט עבור ${role.roleName} בתאריך ${day.dateDay}`}
                                  >
                                    +
                                  </button>
                                )}
                              </div>
                            );
                          }

                          return (
                            <div
                              key={`${role.id}-${day.dateDay}`}
                              data-label={`${day.weekdayLabel} · ${formatDateDot(day.dateDay)}`}
                              className={`${classes.scheduleSlotCell} ${classes.filled}`}
                            >
                              <div className={classes.slotContent}>
                                <p className={classes.slotHours}>
                                  {slot.startHour} - {slot.endHour}
                                </p>
                                {slot.employeePk === null ? (
                                  <p className={classes.slotUnassigned}>לא שובץ עובד</p>
                                ) : (
                                  (() => {
                                    const summary = employeeInsightSummaries.get(slot.employeePk);
                                    return (
                                      <div className={classes.slotEmployeeInsightRow}>
                                        <p className={classes.slotEmployeeName}>
                                          {slot.employeeName ?? 'עובד לא מזוהה'}
                                        </p>
                                        {user?.role === 'manager' && summary && (
                                          <>
                                            <span
                                              className={classes.slotEmployeeDayCounts}
                                              title={`${summary.requestedDays} ימים ביקש, ${summary.receivedDays} ימים קיבל`}
                                              aria-label={`${summary.requestedDays} ימים ביקש, ${summary.receivedDays} ימים קיבל`}
                                            >
                                              {summary.requestedDays} ({summary.receivedDays})
                                            </span>
                                            <button
                                              type="button"
                                              className={`${classes.employeeInsightButton} ${summary.hasWeeklyNote ? classes.hasNote : classes.noNote}`}
                                              aria-label={`פרטי עובד ${slot.employeeName ?? ''}; ${summary.hasWeeklyNote ? 'יש הערה שבועית' : 'אין הערה שבועית'}`}
                                              title={
                                                summary.hasWeeklyNote ? 'יש הערה שבועית' : 'אין הערה שבועית'
                                              }
                                              onClick={() => {
                                                void onOpenEmployeeInsight(slot.employeePk as number);
                                              }}
                                              disabled={isLoadingEmployeeInsight}
                                            >
                                              i
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    );
                                  })()
                                )}
                                <div className={classes.slotActions}>
                                  {canManageSchedule && (
                                    <button
                                      type="button"
                                      className={classes.slotActionButton}
                                      onClick={() => onOpenEditSlotModal(slot)}
                                    >
                                      עריכה
                                    </button>
                                  )}
                                  {canAssignEmployees && slot.employeePk === null && (
                                    <button
                                      type="button"
                                      className={classes.slotActionButton}
                                      onClick={() => onOpenEditSlotModal(slot)}
                                    >
                                      שבץ עובד
                                    </button>
                                  )}
                                  {canAssignEmployees && !canManageSchedule && slot.employeePk !== null && (
                                    <button
                                      type="button"
                                      className={classes.slotActionButton}
                                      onClick={() => onOpenEditSlotModal(slot)}
                                    >
                                      שיבוץ
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </section>
              ))}
            </article>
          ))}
        </div>
      </div>
    )}
  </section>
);
